import { memo, useCallback, useState } from 'react';
import { Container, FormField, Header } from '@amzn/awsui-components-core';
import { TiptapEditor, TiptapOutput, convertStringToTiptapOutput } from '@amzn/awscat-editor';

import CharCountIndicator from '../v2-common/CharCountIndicator';
import styles from '../v2-common/CompactTiptapEditorStyles.module.scss';
import { useEntities } from '../v2-hooks/useEntities';
import { MetadataTabViewModel } from './MetadataTabViewModel';

// Stable references prevent TiptapEditor from re-mounting on every render.
const EMPTY_MENU_ITEMS: never[] = [];
const OUTPUT_FORMATS: ('json' | 'html')[] = ['json', 'html'];

interface MetadataTabDescriptionViewProps {
  readonly vm: MetadataTabViewModel;
}

/** Renders the rich-text Description block at the top of the Metadata tab. */
export const MetadataTabDescriptionView = memo(function MetadataTabDescriptionView({
  vm,
}: MetadataTabDescriptionViewProps) {
  const entities = useEntities();

  // Build the initial Tiptap content once from the projected description string.
  // Subsequent staged updates flow back as plain text via `setContentDescription`,
  // so re-creating the Tiptap document on every render would reset the cursor.
  const [initialContent] = useState(() => convertStringToTiptapOutput(vm.description));

  const handleChange = useCallback(
    (_editor: unknown, output: TiptapOutput) => {
      vm.changeDescription(output);
    },
    [vm],
  );

  return (
    <Container header={<Header variant="h3">Description</Header>}>
      <FormField
        label="Overview"
        description="A short description of this content. Used in search results and listings."
        errorText={vm.fieldErrors.description}
        stretch
      >
        <div
          className={`${styles.container} ${
            vm.fieldErrors.description ? styles.containerError : ''
          }`}
        >
          <TiptapEditor
            content={initialContent}
            menuItems={EMPTY_MENU_ITEMS}
            entities={entities}
            onChange={handleChange}
            outputFormats={OUTPUT_FORMATS}
            editable
            hideMenuBar
            hideFooter
            useContainer={false}
          />
        </div>
        <CharCountIndicator
          convertedLength={vm.descriptionLength}
          min={vm.descriptionLengthBounds.min}
          max={vm.descriptionLengthBounds.max}
          isValidationErrorVisible={!!vm.fieldErrors.description}
          isCountPending={vm.isDescriptionCharCountPending}
        />
      </FormField>
    </Container>
  );
});
